/* global DetailsModal, CompareUtil, trapFocus */

let compareSectionNavSliderCount = 0;

Promise.all([customElements.whenDefined('details-modal'), customElements.whenDefined('compare-drawer')]).then(() => {
  class CompareModal extends DetailsModal {
    constructor() {
      super();
      this.compareContainer = this.querySelector('.compare-container');
      this.compareArea = this.compareContainer.querySelector('.js-compare-area');
      this.toolbar = this.compareArea.querySelector('.compare-modal__toolbar');
      this.differencesToggle = this.toolbar.querySelector('.js-compare-differences-toggle')?.closest('.compare-differences-toggle');
      this.loadingSpinner = this.compareContainer.querySelector('.loading-spinner');
      this.closeButton = this.querySelectorAll('.js-compare-close');
      this.onlyDifferences = false;
      this.urlStatePushed = false;
      this.renderId = 0;
      this.mobileMediaQuery = window.matchMedia('(max-width: 768.98px)');
      this.maxCompareMobile = parseInt(document.querySelector('compare-drawer')?.dataset.maxCompareMobile, 10) || 2;

      this.bindEvents();
      queueMicrotask(() => this.restoreFromUrl());
    }

    disconnectedCallback() {
      this.stickyActionsObserver?.disconnect();
      this.compareUspsObserver?.disconnect();
      cancelAnimationFrame(this.compareUspsFrame);
      document.removeEventListener('click', this.clickHandler);
      document.removeEventListener('on:compare-products:updated', this.compareUpdatedHandler);
      window.removeEventListener('popstate', this.popstateHandler);
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this.mediaQueryHandler);
      } else {
        this.mobileMediaQuery.removeListener(this.mediaQueryHandler);
      }

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:load', CompareModal.handleSectionChange);
        document.removeEventListener('on:product-compare:reload', this.compareReloadHandler);
      }
    }

    bindEvents() {
      this.addEventListener('click', async (evt) => {
        const link = evt.target.closest('.compare-modal__meety-link');
        if (!link) return;
        evt.preventDefault();
        evt.stopImmediatePropagation();
        if (this.meetyOpening) return;
        this.meetyOpening = true;
        const status = this.querySelector('.compare-modal__meety-status');
        if (status) status.textContent = '';
        link.setAttribute('aria-busy', 'true');
        try {
          const deadline = Date.now() + 5000;
          while (!window.Meety?.showModalByURL && Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          if (!window.Meety?.showModalByURL) throw new Error('Meety unavailable');
          await window.Meety.showModalByURL(link.href);
        } catch (error) {
          if (status) status.textContent = status.dataset.error;
        } finally {
          link.removeAttribute('aria-busy');
          this.meetyOpening = false;
        }
      }, true);
      this.addEventListener('on:product-compare-modal:open', this.open);
      this.addEventListener('change', (evt) => {
        const differencesToggle = evt.target.closest('.js-compare-differences-toggle');
        if (!differencesToggle) return;

        this.onlyDifferences = differencesToggle.checked;
        this.compareArea.classList.toggle('compare-area--differences-only', this.onlyDifferences);
        CompareModal.updateDifferencesVisibility(this.compareArea, this.onlyDifferences);
      });

      this.clickHandler = this.handleClick.bind(this);
      document.addEventListener('click', this.clickHandler);
      this.compareUpdatedHandler = this.handleCompareUpdated.bind(this);
      document.addEventListener('on:compare-products:updated', this.compareUpdatedHandler);
      this.popstateHandler = this.handlePopstate.bind(this);
      window.addEventListener('popstate', this.popstateHandler);
      this.mediaQueryHandler = () => {
        if (this.detailsEl.open) this.renderCompareTable();
      };
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.mediaQueryHandler);
      } else {
        this.mobileMediaQuery.addListener(this.mediaQueryHandler);
      }

      this.closeButton.forEach((closeButton) => {
        closeButton.addEventListener('click', this.close.bind(this));
      });

      // Refresh the compare when the Compare section get updated
      if (Shopify.designMode) {
        this.compareReloadHandler = this.renderCompareTable.bind(this);
        document.addEventListener('shopify:section:load', CompareModal.handleSectionChange);
        document.addEventListener('on:product-compare:reload', this.compareReloadHandler);
      }
    }

    /**
     * Handles a 'click' event for remove from compare
     * @param {object} evt - Event object
     */
    handleClick(evt) {
      const colorButton = evt.target.closest('.compare-color-swatch');
      if (colorButton && this.contains(colorButton)) {
        const info = colorButton.closest('.compare-product-info');
        const image = info?.querySelector('.compare-product-info__media-inner img');
        const mediaLink = info?.querySelector('.compare-product-info__media-inner');
        if (image && mediaLink && colorButton.dataset.compareColorImage) {
          image.src = colorButton.dataset.compareColorImage;
          image.removeAttribute('data-srcset');
          image.removeAttribute('srcset');
          image.alt = colorButton.dataset.compareColorTitle || '';
          mediaLink.href = colorButton.dataset.compareColorUrl;
          mediaLink.setAttribute('aria-label', colorButton.dataset.compareColorTitle || '');
          info.querySelectorAll('.compare-color-swatch').forEach((button) => {
            button.classList.toggle('is-selected', button === colorButton);
            button.setAttribute('aria-pressed', String(button === colorButton));
          });
          info.querySelectorAll('.btn--compare-view').forEach((link) => {
            link.href = colorButton.dataset.compareColorUrl;
          });
        }
        return;
      }

      const shareButton = evt.target.closest('.js-compare-share');
      if (shareButton) {
        this.shareComparison();
        return;
      }

      const removeButton = evt.target.closest('.js-compare-col-remove');
      if (removeButton) {
        evt.preventDefault();

        CompareUtil.removeFromCompare(removeButton.dataset.productId.toString(), true);

        const compareCount = CompareUtil.getCompareCount();
        this.compareContainer.dataset.compareCount = compareCount;
        this.updateCompareContainerWidth(compareCount, Boolean(this.compareArea.querySelector('.compare-table')));
        this.observeStickyActions();
        this.observeCompareUsps();
        if (compareCount === 1) {
          this.close();
        } else if (this.mobileMediaQuery.matches) {
          this.renderCompareTable();
        }
        return;
      }

      const sectionButton = evt.target.closest('[data-compare-target]');
      if (sectionButton) {
        evt.preventDefault();
        const target = this.compareArea.querySelector(`#${sectionButton.dataset.compareTarget}`);
        if (target) {
          const modalWindow = this.querySelector('.modal__window');
          const stickyProducts = this.compareArea.querySelector('.compare-table__action-products');
          const stickyHeight = this.mobileMediaQuery.matches ? 0 : (stickyProducts?.getBoundingClientRect().height || 0);
          const targetTop = target.getBoundingClientRect().top
            - modalWindow.getBoundingClientRect().top
            + modalWindow.scrollTop
            - stickyHeight
            - 16;
          modalWindow.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
      }
    }

    /** Uses native sharing on touch devices and copies the link elsewhere. */
    async shareComparison() {
      this.updateShareUrl(false);
      if (!new URL(window.location.href).searchParams.has('compare')) return;

      const status = this.querySelector('.js-compare-share-status');
      const shareButton = this.querySelector('.js-compare-share');
      status.textContent = '';
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title: this.querySelector('.compare-modal__toolbar .compare-heading').textContent.trim(), url: window.location.href });
        } catch {
          // Closing the share sheet must not trigger a second clipboard action.
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(window.location.href);
        status.textContent = shareButton.dataset.linkCopied;
      } catch (error) {
        status.textContent = shareButton.dataset.shareFailed;
      }
    }

    /**
     * Opens the compare modal
     */
    open(options = {}) {
      if (this.detailsEl.open) {
        if (!this.detailsEl.classList.contains('is-closing')) return;
        this.detailsEl.classList.remove('is-closing');
        this.detailsEl.removeEventListener('transitionend', this.transitionendHandler);
      }
      if (!options.fromUrl) this.urlStatePushed = this.updateShareUrl(true);
      const opener = options.detail?.opener
        || (options.fromUrl ? document.querySelector('compare-drawer .js-trigger-compare') : document.activeElement);
      this.modal.classList.remove('hidden');
      this.onlyDifferences = Boolean(this.differencesToggle) && options.detail?.onlyDifferences === true;
      this.renderCompareTable();
      super.open();
      this.opener = opener;
    }

    /**
     * Closes the comparison and returns to the URL before it was opened.
     */
    close() {
      if (!this.detailsEl.open || this.detailsEl.classList.contains('is-closing')) return;
      const opener = this.opener;
      this.opener = null;
      super.close();
      const focusTarget = opener?.isConnected && !opener.disabled
        ? opener
        : document.querySelector('compare-drawer:not([hidden]) .js-clear-compare, #compare-switch, .js-compare-checkbox');
      focusTarget?.focus();

      if (this.handlingPopstate || !new URL(window.location.href).searchParams.has('compare')) return;
      if (this.urlStatePushed) {
        this.urlStatePushed = false;
        window.history.back();
      } else {
        CompareModal.removeShareUrl();
      }
    }

    /**
     * Keeps a copied comparison link in sync when a product is removed in the modal.
     */
    handleCompareUpdated() {
      if (this.detailsEl.open && new URL(window.location.href).searchParams.has('compare')) {
        this.updateShareUrl(false);
      }
    }

    updateCompareContainerWidth(compareCount, hasProductInfo) {
      this.compareContainer.style.setProperty('--compare-columns', compareCount);
      this.compareContainer.style.maxWidth = hasProductInfo && compareCount >= 2 && !this.mobileMediaQuery.matches
        ? `${272 + 400 * Math.max(4, compareCount)}px`
        : '';
    }

    observeStickyActions() {
      this.stickyActionsObserver?.disconnect();

      const actionRow = this.compareArea.querySelector('.compare-table__actions');
      const stickySentinels = [...this.compareArea.querySelectorAll('.compare-table__products .compare-product-info__media')];
      if (!actionRow) return;
      if (!stickySentinels.length) {
        actionRow.classList.add('is-visible');
        return;
      }

      const modalWindow = this.querySelector('.modal__window');
      const updateVisibility = () => {
        const modalTop = modalWindow.getBoundingClientRect().top;
        actionRow.classList.toggle('is-visible', stickySentinels.every((sentinel) => sentinel.getBoundingClientRect().bottom <= modalTop));
      };

      this.stickyActionsObserver = new IntersectionObserver(updateVisibility, { root: modalWindow });
      stickySentinels.forEach((sentinel) => this.stickyActionsObserver.observe(sentinel));
      updateVisibility();
    }

    observeCompareUsps() {
      this.compareUspsObserver?.disconnect();
      cancelAnimationFrame(this.compareUspsFrame);

      const productHeader = this.compareArea.querySelector('.compare-table__products');
      const uspGroups = [...(productHeader?.querySelectorAll('.compare-product-info__usps') || [])];
      if (uspGroups.length < 2) return;

      const alignUsps = () => {
        productHeader.style.removeProperty('--compare-usps-height');
        const height = Math.ceil(Math.max(...uspGroups.map((group) => group.getBoundingClientRect().height)));
        productHeader.style.setProperty('--compare-usps-height', `${height}px`);
      };

      this.compareUspsObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.compareUspsFrame);
        this.compareUspsFrame = requestAnimationFrame(alignUsps);
      });
      uspGroups.forEach((group) => this.compareUspsObserver.observe(group));
      alignUsps();
    }

    /**
     * Restores or closes the comparison when navigating through browser history.
     */
    handlePopstate() {
      this.urlStatePushed = window.history.state?.fahrradCompareModal === true;
      if (new URL(window.location.href).searchParams.has('compare')) {
        this.restoreFromUrl();
      } else if (this.detailsEl.open && !this.detailsEl.classList.contains('is-closing')) {
        this.handlingPopstate = true;
        this.close();
        this.handlingPopstate = false;
      }
    }

    /**
     * Restores the selected products from a bounded, same-origin comparison link.
     */
    restoreFromUrl() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('compare')) return;

      const maxCompare = Number(document.querySelector('compare-drawer')?.dataset.maxCompare) || 4;
      const products = CompareModal.parseShareProducts(url.searchParams.get('compare'), maxCompare);
      if (!products) {
        CompareModal.removeShareUrl();
        return;
      }

      if (!window.history.state?.fahrradCompareModal) {
        const baseUrl = new URL(url);
        baseUrl.searchParams.delete('compare');
        window.history.replaceState(window.history.state, '', baseUrl);
        window.history.pushState({ ...window.history.state, fahrradCompareModal: true }, '', url);
      }
      this.urlStatePushed = true;

      CompareUtil.setSelectedProducts(products);
      CompareUtil.updateCompareCheckboxes();
      CompareUtil.updateCompareCounters(products.length);
      if (!this.detailsEl.open || this.detailsEl.classList.contains('is-closing')) {
        this.open({ fromUrl: true });
      } else {
        this.renderCompareTable();
      }
    }

    /**
     * Writes the current product selection to the address bar.
     * @param {boolean} push - Whether opening the modal should create a history entry.
     * @returns {boolean} Whether a share URL was written.
     */
    updateShareUrl(push) {
      const products = CompareUtil.getSelectedProducts();
      const entries = products.map((product) => {
        const path = CompareModal.productPath(product.url);
        return /^\d+$/.test(String(product.id)) && path ? `${product.id}:${path}` : null;
      });
      if (entries.length < 2 || entries.some((entry) => !entry)) return false;

      const url = new URL(window.location.href);
      const xCloudCollection = url.pathname.match(/^\/a\/search\/([a-z0-9][a-z0-9-]*)\/?$/i);
      if (xCloudCollection) {
        url.pathname = `/collections/${xCloudCollection[1]}`;
        url.search = '';
      }
      url.searchParams.set('compare', entries.join(','));
      if (url.href === window.location.href) return false;

      const state = push ? { ...window.history.state, fahrradCompareModal: true } : window.history.state;
      window.history[push ? 'pushState' : 'replaceState'](state, '', url);
      return true;
    }

    /**
     * Returns a local product path, excluding query parameters and external URLs.
     * @param {string} productUrl - Product URL from the compare selection or share link.
     * @returns {string|null} Valid product path.
     */
    static productPath(productUrl) {
      try {
        const url = new URL(productUrl, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)
          ? url.pathname : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Parses product IDs and paths from an untrusted share URL.
     * @param {string} value - The compare query parameter.
     * @param {number} maxCompare - Configured product limit.
     * @returns {Array|null} Validated product selection.
     */
    static parseShareProducts(value, maxCompare) {
      if (!value || value.length > 2048) return null;
      const entries = value.split(',');
      if (entries.length < 2 || entries.length > maxCompare) return null;

      const products = entries.map((entry) => {
        const separator = entry.indexOf(':');
        const id = entry.slice(0, separator);
        const path = CompareModal.productPath(entry.slice(separator + 1));
        return separator > 0 && /^\d+$/.test(id) && path ? { id, url: path } : null;
      });
      if (products.some((product) => !product)
        || new Set(products.map((product) => product.id)).size !== products.length) return null;
      return products;
    }

    /**
     * Removes the comparison parameter while retaining other page parameters.
     */
    static removeShareUrl() {
      const url = new URL(window.location.href);
      url.searchParams.delete('compare');
      window.history.replaceState(window.history.state, '', url);
    }

    /**
     * Hook into the super function to hide the modal when it's finished closing
     * @param {object} evt - Event object
     */
    handleTransitionEnd(evt) {
      super.handleTransitionEnd(evt);
      if (this.modal.open === false) {
        this.modal.classList.add('hidden');
      }
    }

    /**
     * Triggers a refresh the compare table based on Theme Editor events
     * @param {object} evt - Event object
     */
    static handleSectionChange(evt) {
      if (evt.target.matches('.cc-compare')) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('on:product-compare:reload'));
        }, 1000);
      }
    }

    /**
     * Renders the compare table
     */
    async renderCompareTable() {
      const renderId = ++this.renderId;
      this.stickyActionsObserver?.disconnect();
      this.compareUspsObserver?.disconnect();
      cancelAnimationFrame(this.compareUspsFrame);
      // Reset the previous state if applicable
      this.loadingSpinner.removeAttribute('hidden');
      this.loadingSpinner.removeAttribute('aria-hidden');
      this.compareArea.classList.remove('revealable--in');
      this.compareArea.classList.toggle('compare-area--differences-only', this.onlyDifferences);
      if (this.differencesToggle) {
        this.toolbar.appendChild(this.differencesToggle);
        this.differencesToggle.querySelector('.js-compare-differences-toggle').checked = this.onlyDifferences;
      }
      this.compareArea.innerHTML = '';

      // Get the compare products
      const selectedProducts = [...new Map(CompareUtil.getSelectedProducts().map((product) =>
        [CompareModal.productPath(product.url), product])).values()];
      const compareProducts = this.mobileMediaQuery.matches
        ? selectedProducts.slice(0, this.maxCompareMobile)
        : selectedProducts;
      if (compareProducts) {
        this.compareContainer.dataset.compareCount = compareProducts.length;

        // Transform the HTML into something flexbox friendly
        const compareProductHtmlArr = await CompareModal.getCompareProductsWithTimeout(compareProducts);
        if (renderId !== this.renderId) return;
        const compareFieldsArr = {};

        if (compareProductHtmlArr.length > 0) {
          CompareUtil.setSelectedProducts(selectedProducts);
          CompareUtil.updateCompareCheckboxes();
          CompareUtil.updateCompareCounters(selectedProducts.length);
          this.updateShareUrl(false);
          compareProductHtmlArr.forEach((compareProductHtml, productIndex) => {
            const responseHTML = document.implementation.createHTMLDocument();
            responseHTML.documentElement.innerHTML = compareProductHtml; // eslint-disable-line

            responseHTML.querySelectorAll('.shopify-section > div').forEach((compareField) => {
              if (!compareFieldsArr[compareField.dataset.compareKey]) {
                compareFieldsArr[compareField.dataset.compareKey] = new Array(compareProducts.length).fill(null);
              }
              compareFieldsArr[compareField.dataset.compareKey][productIndex] = compareField;
            });
          });

          // Create the compare flex
          const compareElem = document.createElement('div');
          compareElem.className = 'compare-temp';

          const allEntries = Object.entries(compareFieldsArr);
          const actionsEntry = allEntries.find(([key]) => key === 'compare-actions');
          const otherEntries = CompareModal.sortCompareEntries(
            allEntries.filter(([key]) => key !== 'compare-actions')
          );
          const productInfoEntry = allEntries.find(([key]) => key === 'compare-product_info');
          this.updateCompareContainerWidth(compareProducts.length, Boolean(productInfoEntry));

          if (productInfoEntry) {
            compareElem.appendChild(CompareModal.createCompareTable(productInfoEntry, otherEntries, compareProducts, actionsEntry, this.mobileMediaQuery.matches));
          } else {
            [...otherEntries, ...(actionsEntry ? [actionsEntry] : [])].forEach(([key, value]) => {
              compareElem.appendChild(CompareModal.createCompareRow(key, value, compareProducts));
            });
          }

          this.compareArea.innerHTML = compareElem.innerHTML;
          const sectionNavHeader = this.compareArea.querySelector('.compare-section-nav__header');
          if (sectionNavHeader) {
            if (!this.mobileMediaQuery.matches) {
              const controls = document.createElement('div');
              controls.className = 'compare-section-nav__controls';
              const sectionNav = this.compareArea.querySelector('.compare-section-nav');
              if (sectionNav) controls.appendChild(sectionNav);
              if (this.differencesToggle) controls.appendChild(this.differencesToggle);
              sectionNavHeader.appendChild(controls);
            } else if (this.differencesToggle) {
              sectionNavHeader.appendChild(this.differencesToggle);
            }
          }
        } else {
          this.compareArea.innerHTML = `Unable to compare products.${
            Shopify.designMode
              ? ' Ensure the Product Compare section is enabled.'
              : ' Please try again later.'
          }`;
        }
        const compareTable = this.compareArea.querySelector('.compare-table');
        (compareTable || this.compareArea).prepend(this.toolbar);
        CompareModal.updateDifferencesVisibility(this.compareArea, this.onlyDifferences);
        this.observeStickyActions();
        this.observeCompareUsps();

        // Timeout to trigger CSS transition
        setTimeout(() => {
          if (renderId !== this.renderId) return;
          this.loadingSpinner.setAttribute('hidden', 'true');
          this.loadingSpinner.setAttribute('aria-hidden', 'true');
          this.compareArea.classList.add('revealable--in');
          if (this.detailsEl.open && !this.detailsEl.classList.contains('is-closing')) {
            const activeElement = document.activeElement;
            trapFocus(this.detailsEl, this.detailsEl.contains(activeElement)
              ? activeElement : this.querySelector('.modal__window'));
          }
        }, 100);
      }
    }

    static sortCompareEntries(entries) {
      const excludedLabels = new Set([
        'ean',
        'herstellerartikelnummer',
        'herstellermodellnummer',
        'herstellerartikelnr',
        'herstellermodellnr'
      ]);
      entries = entries.filter(([key, values]) => {
        const label = CompareModal.getCompareRowLabel(values.find(Boolean), key)
          .toLocaleLowerCase('de').replace(/[^a-z0-9]/g, '');
        const fieldKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
        return !excludedLabels.has(label)
          && ![...excludedLabels].some((excluded) => fieldKey.endsWith(excluded));
      });
      const nonSpecificationEntries = entries.filter(([key]) => !key.startsWith('compare-spec-'));
      const specificationEntries = entries.filter(([key]) => key.startsWith('compare-spec-'));
      const categories = specificationEntries
        .filter(([key]) => key.startsWith('compare-spec-header-'))
        .map((entry) => ({
          entry,
          key: entry[0].replace('compare-spec-header-', '')
        }));
      const groupedSpecificationEntries = categories.flatMap((category) => {
        const fields = specificationEntries
          .filter(([key]) => key.startsWith(`compare-spec-${category.key}-`))
          .sort(([keyA, valueA], [keyB, valueB]) => CompareModal.getCompareRowLabel(valueA.find(Boolean), keyA)
            .localeCompare(CompareModal.getCompareRowLabel(valueB.find(Boolean), keyB), 'de'));
        return [category.entry, ...fields];
      });
      const groupedKeys = new Set(groupedSpecificationEntries.map(([key]) => key));
      const ungroupedSpecificationEntries = specificationEntries.filter(([key]) => !groupedKeys.has(key));

      return [...nonSpecificationEntries, ...groupedSpecificationEntries, ...ungroupedSpecificationEntries];
    }

    /**
     * Hides rows whose rendered values are complete and identical across products.
     * Incomplete or empty rows stay visible so missing data is never mistaken for a match.
     * @param {HTMLElement} container - Rendered comparison area.
     * @param {boolean} enabled - Whether identical rows should be hidden.
     */
    static updateDifferencesVisibility(container, enabled) {
      const rows = [...container.querySelectorAll('.compare-row')];
      rows.forEach((row) => {
        if (row.classList.contains('compare-row--spec-header')
          || row.classList.contains('compare-table__products')
          || row.classList.contains('compare-table__actions')
          || row.classList.contains('compare-row--actions')) {
          return;
        }

        const columns = [...row.querySelectorAll(':scope > .compare-col')];
        const values = columns.map((column) => {
          if (column.dataset.comparePlaceholder === 'true' || column.dataset.isEmptyMetafield === 'true') return null;

          const valueClone = column.cloneNode(true);
          valueClone.querySelectorAll('.compare-label, .compare-feature-badge, button, input, select, textarea').forEach((element) => element.remove());
          const value = valueClone.textContent.replace(/\s+/g, ' ').normalize('NFC').trim();
          return value || null;
        });

        const completeValues = values.length === Number(container.closest('compare-modal')?.compareContainer?.dataset.compareCount)
          && values.length > 1
          && values.every((value) => value !== null);
        const identical = completeValues && values.every((value) => value === values[0]);
        row.dataset.compareIdentical = identical ? 'true' : 'false';
        row.hidden = enabled && identical;
      });

      container.querySelectorAll('.compare-table__group-heading').forEach((heading) => {
        const group = heading.nextElementSibling;
        const hasVisibleRows = Boolean(group?.querySelector('.compare-row:not([hidden])'));
        heading.hidden = enabled && !hasVisibleRows;
        if (group?.classList.contains('compare-table__specs')) {
          group.hidden = enabled && !hasVisibleRows;
        }
      });
    }

    /**
     * Creates the product-card header and comparison table used by the combined product info block.
     * @param {Array} productInfoEntry - Product info compare entry.
     * @param {Array} entries - Compare entries in section order.
     * @param {Array} compareProducts - Compared products.
     * @returns {HTMLElement}
     */
    static createCompareTable(productInfoEntry, entries, compareProducts, actionsEntry, isMobile = false) {
      const compareTable = document.createElement('div');
      compareTable.className = 'compare-table';
      compareTable.id = 'compare-section-overview';

      const productHeader = document.createElement('div');
      productHeader.className = 'compare-row compare-table__products compare-row--product_info';

      const [, productInfoValue] = productInfoEntry;
      const productInfoFields = productInfoValue.filter(Boolean);
      productInfoValue.forEach((elem, productIndex) => {
        productHeader.appendChild(
          elem || CompareModal.createPlaceholderColumn(
            productInfoFields[0],
            compareProducts[productIndex],
            'compare-product_info'
          )
        );
      });
      compareTable.appendChild(productHeader);
      compareTable.appendChild(CompareModal.createCompareSectionNav(entries));

      let specs;
      entries.forEach(([key, value]) => {
        if (key !== 'compare-product_info') {
          const isGroupHeading = key.startsWith('compare-spec-header-');
          if (isGroupHeading) {
            const heading = CompareModal.createCompareTableLabelColumn(value.find(Boolean), key, true, false);
            heading.classList.add('compare-table__group-heading', 'compare-heading');
            heading.id = CompareModal.getCompareSectionId(key);
            compareTable.appendChild(heading);
          }
          if (!specs || isGroupHeading) {
            specs = document.createElement('div');
            specs.className = 'compare-table__specs';
            compareTable.appendChild(specs);
          }
          if (!isGroupHeading) {
            specs.appendChild(CompareModal.createCompareRow(key, value, compareProducts, true));
          }
        }
      });

      if (actionsEntry) {
        const actionRow = document.createElement('div');
        actionRow.className = 'compare-row compare-row--actions compare-table__actions';
        actionRow.appendChild(CompareModal.createCompareTableSpacerColumn());
        const actionProducts = document.createElement('div');
        actionProducts.className = 'compare-table__action-products';
        actionRow.appendChild(actionProducts);

        const [, actionValues] = actionsEntry;
        const actionFields = actionValues.filter(Boolean);
        actionValues.forEach((elem, productIndex) => {
          actionProducts.appendChild(
            elem || CompareModal.createPlaceholderColumn(
              actionFields[0],
              compareProducts[productIndex],
              'compare-actions'
            )
          );
        });
        if (isMobile) {
          compareTable.appendChild(actionRow);
        } else {
          compareTable.prepend(actionRow);
        }
      }

      return compareTable;
    }

    /**
     * Creates a horizontally scrollable navigation for the overview and specification groups.
     * @param {Array} entries - Compare entries in section order.
     * @returns {DocumentFragment}
     */
    static createCompareSectionNav(entries) {
      const sliderId = `compare-section-nav-${++compareSectionNavSliderCount}`;
      const template = document.createElement('template');
      template.innerHTML = `
        <div class="compare-section-nav__header">
          <h3 class="compare-heading compare-section-nav__title">${theme.strings.compare.specification}</h3>
        </div>
        <carousel-slider class="compare-section-nav carousel block metafield-slider">
          <div class="container relative">
            <span class="compare-section-nav__intro"></span>
            <div class="compare-section-nav__viewport" data-overlayscrollbars="host">
              <div class="slider" id="${sliderId}">
                <ul class="slider__grid grid grid-flow-col grid-auto-width gap-x-theme gap-y-4" role="list"></ul>
              </div>
            </div>
          </div>
        </carousel-slider>`;
      const nav = template.content.querySelector('carousel-slider');
      nav.querySelector('.compare-section-nav__intro').textContent = theme.strings.compare.jumpTo;
      nav.setAttribute('aria-label', theme.strings.compare.navigation);

      const list = nav.querySelector('.slider__grid');

      const sections = [];
      entries.forEach(([key, value]) => {
        if (key.startsWith('compare-spec-header-')) {
          sections.push([
            CompareModal.getCompareSectionId(key),
            CompareModal.getCompareRowLabel(value.find(Boolean), key)
          ]);
        }
      });

      sections.forEach(([targetId, label]) => {
        const item = document.createElement('li');
        item.className = 'compare-section-nav__item slider__item';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'text-link';
        button.dataset.compareTarget = targetId;
        button.textContent = label;

        item.appendChild(button);
        list.appendChild(item);
      });

      return template.content;
    }

    /**
     * Creates a stable DOM id for a specification group.
     * @param {string} key - Compare section key.
     * @returns {string}
     */
    static getCompareSectionId(key) {
      return `compare-section-${key.replace(/[^a-z0-9_-]/gi, '-')}`;
    }

    /**
     * Creates a compare row in either legacy mode or table mode.
     * @param {string} key - Compare row key.
     * @param {Array} value - Product columns for the row.
     * @param {Array} compareProducts - Compared products.
     * @param {boolean} tableMode - Whether to render a dedicated left label column.
     * @returns {HTMLElement}
     */
    static createCompareRow(key, value, compareProducts, tableMode = false) {
      const compareFields = value.filter(Boolean);
      const isSpecRow = key.startsWith('compare-spec-');
      const isSpecHeaderRow = key.startsWith('compare-spec-header-');

      // If we can show empty metafield rows, or all metafields are not empty
      if (!theme.settings.compareShowEmptyMetafields
        && value.every((elem) => !elem || !!elem.dataset.isEmptyMetafield)) {
        return document.createDocumentFragment();
      }

      const compareRow = document.createElement('div');
      compareRow.className = `compare-row compare-row--${
        key.includes('metafield') ? 'metafield' : key.replace('compare-', '')
      }`;
      if (tableMode) {
        compareRow.classList.add('compare-table__row');
      }
      if (isSpecHeaderRow) {
        compareRow.classList.add('compare-row--spec-header');
      }

      if (tableMode) {
        compareRow.appendChild(CompareModal.createCompareTableLabelColumn(compareFields[0], key, isSpecHeaderRow));
      } else if (isSpecRow) {
        compareRow.appendChild(CompareModal.createCompareLabelColumn(compareFields[0], isSpecHeaderRow));
      }

      value.forEach((elem, productIndex) => {
        const compareColumn = elem || CompareModal.createPlaceholderColumn(
          compareFields[0],
          compareProducts[productIndex],
          key,
          !tableMode && !isSpecRow && productIndex === 0
        );
        compareRow.appendChild(CompareModal.prepareCompareColumn(compareColumn, tableMode || isSpecRow, isSpecHeaderRow));
      });

      if (isSpecRow && value.some((column) => column?.dataset.compareHighlight)) {
        CompareModal.highlightCompareValue(value);
      }

      return compareRow;
    }

    /**
     * Highlights a comparable numeric specification when at least two products have matching values.
     * @param {Array} columns - Product columns for the specification row.
     */
    static highlightCompareValue(columns) {
      if (columns.length < 2) return;

      const rules = {
        weight: { direction: 'min', label: 'lightest' },
        battery: { direction: 'max', label: 'largestBattery' },
        torque: { direction: 'max', label: 'highestTorque' }
      };
      const type = columns.find((column) => column?.dataset.compareHighlight)?.dataset.compareHighlight;
      const rule = rules[type];
      if (!rule) return;

      const values = columns.map((column) => {
        if (!column || column.dataset.compareHighlight !== type) return null;
        const sourceValue = CompareModal.parseCompareValue(column.dataset.compareSource, type);
        const displayedValue = CompareModal.parseCompareValue(column.querySelector('.compare-value')?.textContent, type);
        return sourceValue === displayedValue ? sourceValue : null;
      });
      const comparableValues = values.filter((value) => value !== null);
      if (comparableValues.length < 2) return;

      const highlightedValue = Math[rule.direction](...comparableValues);
      if (comparableValues.every((value) => value === highlightedValue)) return;

      columns.forEach((column, index) => {
        if (values[index] !== highlightedValue) return;

        CompareModal.addCompareBadge(column, theme.strings.compare[rule.label]);
      });
    }

    /**
     * Adds a translated badge to an existing comparison value.
     * @param {HTMLElement} column - Product column.
     * @param {string} label - Badge text.
     */
    static addCompareBadge(column, label) {
      column.classList.add('compare-col--highlighted');
      const badge = document.createElement('span');
      badge.className = 'compare-feature-badge';
      badge.textContent = label;
      column.querySelector('.compare-value').appendChild(badge);
    }

    /**
     * Converts supported specifications to a common unit without accepting other text.
     * @param {string} value - Value with unit.
     * @param {string} type - Comparison type.
     * @returns {number|null}
     */
    static parseCompareValue(value, type) {
      const units = {
        weight: /^(\d+(?:[.,]\d+)?)\s*(kg|g)$/i,
        battery: /^(\d+(?:[.,]\d+)?)\s*(kwh|wh)$/i,
        torque: /^(\d+(?:[.,]\d+)?)\s*nm$/i
      };
      const match = value?.trim().match(units[type]);
      if (!match) return null;

      const number = Number(match[1].replace(',', '.'));
      if (number <= 0) return null;
      return number * (match[2] && ['kg', 'kwh'].includes(match[2].toLowerCase()) ? 1000 : 1);
    }

    /**
     * Creates the empty top-left spacer used to align product cards with table values.
     * @returns {HTMLElement}
     */
    static createCompareTableSpacerColumn() {
      const spacer = document.createElement('div');
      spacer.className = 'compare-table__label compare-table__label--spacer';
      spacer.setAttribute('aria-hidden', 'true');
      return spacer;
    }

    /**
     * Creates the static left label column for comparison table rows.
     * @param {HTMLElement} referenceColumn - Column from the same compare row.
     * @param {string} compareKey - Compare row key.
     * @param {boolean} isHeader - Whether this is a specification category header.
     * @returns {HTMLElement}
     */
    static createCompareTableLabelColumn(referenceColumn, compareKey, isHeader = false, includeIcon = true) {
      const labelColumn = document.createElement(isHeader && !includeIcon ? 'h4' : 'div');
      labelColumn.className = `compare-table__label${isHeader ? ' compare-table__label--header' : ''}`;

      const iconUrl = referenceColumn?.dataset.compareIcon;
      if (includeIcon && iconUrl) {
        const icon = document.createElement('img');
        icon.className = 'spec-icon';
        icon.src = iconUrl;
        icon.width = 20;
        icon.height = 20;
        icon.alt = '';
        icon.loading = 'lazy';
        labelColumn.appendChild(icon);
      }

      const label = document.createElement('span');
      label.textContent = CompareModal.getCompareRowLabel(referenceColumn, compareKey);
      labelColumn.appendChild(label);

      return labelColumn;
    }

    /**
     * Resolves a readable label for the left comparison table column.
     * @param {HTMLElement} referenceColumn - Column from the same compare row.
     * @param {string} compareKey - Compare row key.
     * @returns {string}
     */
    static getCompareRowLabel(referenceColumn, compareKey) {
      const label = referenceColumn?.dataset.compareLabel
        || referenceColumn?.querySelector('.compare-label')?.textContent?.trim();

      if (label) return label;

      return compareKey
        .replace('compare-', '')
        .replace(/-/g, ' ');
    }

    /**
     * Creates an empty compare cell for products that do not expose a field.
     * This keeps all rows aligned with the product header columns.
     * @param {HTMLElement} referenceColumn - Column from the same compare row.
     * @param {object} product - Compared product for the placeholder position.
     * @param {string} compareKey - Compare row key.
     * @param {boolean} includeLabel - Whether to include the row label in the placeholder.
     * @returns {HTMLElement}
     */
    static createPlaceholderColumn(referenceColumn, product, compareKey, includeLabel = false) {
      const placeholder = document.createElement('div');
      placeholder.dataset.compareKey = compareKey;
      placeholder.dataset.comparePlaceholder = 'true';

      if (referenceColumn) {
        placeholder.className = referenceColumn.className;
        Array.from(placeholder.classList).forEach((className) => {
          if (className === `compare-col--${product.id}` || /^compare-col--\d/.test(className)) {
            placeholder.classList.remove(className);
          }
        });
      } else {
        placeholder.className = 'compare-col';
      }

      placeholder.classList.add(`compare-col--${product.id}`);
      if (includeLabel && referenceColumn?.querySelector('.compare-label')) {
        placeholder.appendChild(referenceColumn.querySelector('.compare-label').cloneNode(true));
        placeholder.appendChild(document.createElement('div')).className = 'compare-value';
      } else {
        placeholder.setAttribute('aria-hidden', 'true');
      }
      return placeholder;
    }

    /**
     * Creates the static label column for specification rows.
     * @param {HTMLElement} referenceColumn - Column from the same compare row.
     * @param {boolean} isHeader - Whether this is a specification category header.
     * @returns {HTMLElement}
     */
    static createCompareLabelColumn(referenceColumn, isHeader = false) {
      const labelColumn = document.createElement('div');
      labelColumn.className = `compare-row__label${isHeader ? ' compare-row__label--header' : ''}`;

      const iconUrl = referenceColumn?.dataset.compareIcon;
      if (iconUrl) {
        const icon = document.createElement('img');
        icon.className = 'spec-icon';
        icon.src = iconUrl;
        icon.width = 20;
        icon.height = 20;
        icon.alt = '';
        icon.loading = 'lazy';
        labelColumn.appendChild(icon);
      }

      const label = document.createElement('span');
      label.textContent = referenceColumn?.dataset.compareLabel
        || referenceColumn?.querySelector('.compare-label')?.textContent?.trim()
        || referenceColumn?.textContent?.trim()
        || '';
      labelColumn.appendChild(label);

      return labelColumn;
    }

    /**
     * Normalizes product columns for specification rows now that labels live in their own column.
     * @param {HTMLElement} column - Compare product column.
     * @param {boolean} moveLabelToColumn - Whether labels live in a dedicated left column.
     * @param {boolean} isHeader - Whether this is a specification category header.
     * @returns {HTMLElement}
     */
    static prepareCompareColumn(column, moveLabelToColumn = false, isHeader = false) {
      if (!moveLabelToColumn) return column;

      if (isHeader) {
        column.replaceChildren();
        column.setAttribute('aria-hidden', 'true');
        return column;
      }

      column.querySelectorAll('.compare-label').forEach((label) => label.classList.add('compare-label--desktop-only'));

      return column;
    }

    /**
     * Fetches an array of html for each of the compare products, using the section rendering API
     * @param {Array} compareProducts - An array of product objects to compare
     */
    static async getCompareProducts(compareProducts) {
      const compareSection = document.querySelector('.cc-compare[id]');
      const sectionId = compareSection?.id.replace(/^shopify-section-/, '');

      return Promise.all(compareProducts.map(async (product) => {
        try {
          const extractCompareSection = (html) => {
            const tmpl = document.createElement('template');
            tmpl.innerHTML = html;
            const compareEl = tmpl.content.querySelector('.cc-compare');
            const templateEl = compareEl?.querySelector('template');
            const productId = templateEl?.content.querySelector('.compare-product-info[data-product-id], .js-compare-col-remove')?.dataset.productId;
            if (!templateEl || !productId || !/^\d+$/.test(productId)) return null;
            // The product page is authoritative when a search provider supplied a variant ID.
            product.id = productId;

            const templateContent = document.importNode(templateEl.content, true);
            templateEl.parentNode.insertBefore(templateContent, templateEl);
            templateEl.remove();
            return compareEl.outerHTML;
          };

          let compareHtml = null;
          if (sectionId) {
            try {
              const sectionUrl = new URL(product.url, window.location.origin);
              if (sectionUrl.origin === window.location.origin) {
                sectionUrl.searchParams.set('sections', sectionId);
                const sections = await CompareModal.fetchWithTimeout(sectionUrl, 'json');
                compareHtml = sections?.[sectionId] || null;
              }
            } catch (error) {
              // Fall back to the product page if the section response is unavailable.
            }
          }

          const extractedSection = compareHtml ? extractCompareSection(compareHtml) : null;
          if (extractedSection) return extractedSection;

          const productHtml = await CompareModal.fetchWithTimeout(product.url, 'text');
          return productHtml ? extractCompareSection(productHtml) : null;
        } catch (error) {
          // An unavailable product should show the existing comparison error state.
        }
        return null;
      })).then((products) => products.every(Boolean) ? products : []);
    }

    /**
     * Bounds the complete render data request in case a browser keeps an aborted
     * request pending. This is a final safeguard for the loading state.
     * @param {Array} compareProducts - Products to render.
     * @returns {Promise<Array>} Compare section HTML, or an empty array on timeout.
     */
    static async getCompareProductsWithTimeout(compareProducts) {
      let timeoutId;
      const timeout = new Promise((resolve) => {
        timeoutId = setTimeout(() => resolve([]), 10000);
      });

      try {
        return await Promise.race([CompareModal.getCompareProducts(compareProducts), timeout]);
      } finally {
        clearTimeout(timeoutId);
      }
    }

    /**
     * Fetches a compare response with a bounded wait. A stalled product request must not
     * leave the comparison spinner visible indefinitely.
     * @param {URL|string} url - Same-origin endpoint to request.
     * @param {'json'|'text'} responseType - Response body format.
     * @param {number} timeout - Maximum wait in milliseconds.
     * @returns {Promise<object|string|null>} Parsed response or null when unavailable.
     */
    static async fetchWithTimeout(url, responseType, timeout = 8000) {
      const controller = typeof AbortController === 'undefined' ? null : new AbortController();
      const timeoutId = controller ? setTimeout(() => controller.abort(), timeout) : null;

      try {
        const response = await fetch(url, controller ? { signal: controller.signal } : undefined);
        if (!response.ok) return null;
        return responseType === 'json' ? await response.json() : await response.text();
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }
  }

  customElements.define('compare-modal', CompareModal);
});
